<template>
  <div>
    <el-table
      :data="pipes"
      class="w-100"
      border
      stripe
      show-summary
      :summary-method="getSummaries"
    >
      <el-table-column
        :label="tableName"
        align="center"
        :class-name="getTableColourByName(tableName)"
      >
        <el-table-column
          label="State"
          :formatter="row => $_.get(row, 'branch', '-')"
        />

        <el-table-column
          label="Rep"
          :formatter="row => $_.get(row, 'account.portfolio.long', '-')"
        />

        <el-table-column
          label="Last Spoken"
        >
          <template slot-scope="props">
            {{ getLastestCompletedEvent(props.row.account.events) ? $moment(getLastestCompletedEvent(props.row.account.events).due_at).format('DD-MM-YYYY') : '-' }}
          </template>
        </el-table-column>

        <el-table-column
          label="Customer"
          :formatter="row => $_.get(row, 'account.name', '-')"
        />

        <el-table-column
          label="Decision Maker"
          :formatter="row => $_.get(row, 'account.contact', '-')"
        />

        <el-table-column
          label="Annually Spend"
          prop="worth"
          :formatter="row => `$ ${$_.get(row, 'worth', '-')}`"
        />

        <el-table-column
          label="Competitor"
        >
          <template slot-scope="props">
            <template v-if="!$_.isEmpty(props.row.incumbent_carriers)">
              <el-tag
                v-for="c in props.row.incumbent_carriers"
                :key="c.id"
                v-tooltip.top-center="$_.get(c, 'carrier.name') ? $_.get(c, 'carrier.name') : $_.get(c, 'name') || '-'"
                size="mini"
              >
                {{ $_.get(c, 'carrier.name') ? $_.get(c, 'carrier.name') : $_.get(c, 'name') || '-' }}
              </el-tag>
            </template>
            <div v-else>
              -
            </div>
          </template>
        </el-table-column>

        <el-table-column
          label="TIG Carriers"
        >
          <template slot-scope="props">
            <template v-if="!$_.isEmpty(props.row.suggested_carriers)">
              <el-tag
                v-for="c in props.row.suggested_carriers"
                :key="c.id"
                v-tooltip.top-center="$_.get(c, 'carrier.name') ? $_.get(c, 'carrier.name') : $_.get(c, 'name') || '-'"
                size="mini"
              >
                {{ $_.get(c, 'carrier.name') ? $_.get(c, 'carrier.name') : $_.get(c, 'name') || '-' }}
              </el-tag>
            </template>
            <div v-else>
              -
            </div>
          </template>
        </el-table-column>

        <el-table-column
          label="To Win"
          width="70"
        >
          <template slot-scope="props">
            <el-popover
              placement="left"
              title="To Win"
              width="200"
              trigger="hover"
              :content="$_.get(props.row, 'reason', '-')"
            >
              <el-button
                slot="reference"
                type="text"
                size="small"
              >
                To Win
              </el-button>
            </el-popover>
          </template>
        </el-table-column>

        <el-table-column
          label="Next Event"
          width="100"
        >
          <template slot-scope="props">
            {{ getNextEventStartDate(props.row.account.events) ? $moment(getNextEventStartDate(props.row.account.events).due_at).format('DD-MM-YYYY') : '-' }}
          </template>
        </el-table-column>

        <el-table-column
          label="Comments"
          width="110"
        >
          <template slot-scope="props">
            <template v-if="getLastestCompletedEvent(props.row.account.events)">
              <Promised :promise="getNoteByEventId(getLastestCompletedEvent(props.row.account.events).id)">
                <template v-slot:pending>
                  <el-button
                    v-loading="true"
                    type="text"
                    size="small"
                  >
                    Comments
                  </el-button>
                </template>
                <template v-slot="data">
                  <el-popover
                    placement="left"
                    :title="$moment(getLastestCompletedEvent(props.row.account.events).due_at).format('DD-MM-YYYY')"
                    width="200"
                    trigger="hover"
                    :content="$_.get(data[0], 'content', '-')"
                  >
                    <el-button
                      slot="reference"
                      type="text"
                      size="small"
                    >
                      Comments
                    </el-button>
                  </el-popover>
                </template>
              </Promised>
            </template>
            <div v-else>
              No Comments
            </div>
          </template>
        </el-table-column>
      </el-table-column>
    </el-table>
  </div>
</template>

<script>
import { Promised } from 'vue-promised'
import Filter, { IS } from '@/plugins/sdk/Filter'
export default {
  components: {
    Promised
  },
  props: {
    pipes: {
      type: Array,
      required: true
    },
    tableName: {
      type: String,
      required: false,
      default: 'Sales Pipes'
    }
  },
  methods: {
    getTableColourByName (name) {
      switch (name) {
        case 'STARTING': {
          return 'tig-purple'
        }
        case 'AWAITING DECISION': {
          return 'tig-green'
        }
        case 'WORKING ON': {
          return 'tig-blue'
        }
        default: {
          return ''
        }
      }
    },
    getLastestCompletedEvent (events) {
      return events.find(e => e.completed_at !== undefined)
    },
    getNextEventStartDate (events) {
      return events.find(e => e.completed_at === undefined)
    },
    async getNoteByEventId (eventId) {
      const note = await this.$sdk.note.list(builder => {
        builder.filters = [new Filter('resource_uuid', IS, eventId)]
        builder.page = 1
        builder.limit = 1
        builder.order = 'created_at:desc'
      })
      return note
    },
    getSummaries (param) {
      const { columns, data } = param
      const sums = []
      columns.forEach((column, index) => {
        if (index === 0) {
          sums[index] = 'Total'
          return
        }
        const values = data.map(item => Number(item[column.property]))
        if (!values.every(value => isNaN(value))) {
          sums[index] = '$ ' + values.reduce((prev, curr) => {
            const value = Number(curr)
            if (!isNaN(value)) {
              return prev + curr
            } else {
              return prev
            }
          }, 0)
        } else {
          sums[index] = ''
        }
      })

      return sums
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .tig-blue {
  background-color: #2e7bccd6 !important;
  color: white;
}
::v-deep .tig-green {
  background-color: #228c47ab !important;
  color: white;
}
::v-deep .tig-purple {
  background-color: #895597c7 !important;
  color: white;
}
</style>
